import { motion } from "framer-motion";
import { useGameStore } from "../../store/gameStore";
import { useLanguage } from "../../i18n/LanguageContext";
import { rankPlayers, calcNetWorth } from "../../engine/scoring";
import Button from "../ui/Button";
import AdUnit from "../ui/AdUnit";

const MEDALS = ["🥇", "🥈", "🥉", "4️⃣", "5️⃣", "6️⃣"];
const ICON_EMOJI: Record<string, string> = { degree: "🎓", trade_cert: "🔧", skill_badge: "💻", network: "🤝", language: "🌍", leadership: "⭐" };

export default function EndGameScreen() {
  const { players, resetGame } = useGameStore();
  const { t } = useLanguage();
  const ranked = rankPlayers(players);
  const winner = ranked[0];
  const maxNW = calcNetWorth(winner);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 font-display flex items-center justify-center p-4">
      <motion.div className="w-full max-w-lg space-y-6" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <div className="text-center">
          <div className="text-6xl mb-2">🏆</div>
          <h1 className="text-3xl font-black text-white">{t.endgame.gameOver}</h1>
          <p className="text-white/50 mt-1">
            {winner.avatarKey} <span className="text-white font-bold">{t.endgame.wins(winner.name, maxNW)}</span>
          </p>
        </div>

        <div className="space-y-3">
          {ranked.map((player, i) => {
            const nw = calcNetWorth(player);
            const cashPct = nw > 0 ? (player.cash / nw) * 100 : 0;
            const invValue = player.investments.reduce((s, inv) => s + inv.currentValue, 0);
            const invPct = nw > 0 ? (invValue / nw) * 100 : 0;
            const itemValue = player.ownedItems.reduce((s, item) => s + item.resaleValue, 0);
            const itemPct = nw > 0 ? (itemValue / nw) * 100 : 0;

            return (
              <motion.div key={player.id} className="bg-slate-800/80 border border-white/10 rounded-2xl p-4 space-y-3" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.1 }}>
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{MEDALS[i]}</span>
                  <span className="text-2xl">{player.avatarKey}</span>
                  <div className="flex-1">
                    <div className="font-bold text-white">{player.name}</div>
                    <div className="text-gold font-black text-lg">💎 {nw}</div>
                  </div>
                  <div className="text-right text-xs text-white/40 space-y-0.5">
                    <div>{t.endgame.cash} {player.cash}</div>
                    <div>{t.endgame.investments} {invValue}</div>
                    <div>{t.endgame.items} {itemValue}</div>
                  </div>
                </div>

                <div className="h-4 rounded-full overflow-hidden bg-white/5 flex">
                  <div className="bg-gold/80 transition-all" style={{ width: `${cashPct}%` }} />
                  <div className="bg-green-500/80 transition-all" style={{ width: `${invPct}%` }} />
                  <div className="bg-purple-500/80 transition-all" style={{ width: `${itemPct}%` }} />
                </div>
                <div className="flex gap-3 text-xs text-white/40">
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-gold/80 inline-block" /> {t.endgame.cashLegend}</span>
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-green-500/80 inline-block" /> {t.endgame.investLegend}</span>
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-purple-500/80 inline-block" /> {t.endgame.itemsLegend}</span>
                </div>

                {player.prepIcons.length > 0 && (
                  <div className="text-xs text-white/40">
                    {t.endgame.skills} {[...new Set(player.prepIcons)].map((icon) => {
                      const count = player.prepIcons.filter((i) => i === icon).length;
                      return `${ICON_EMOJI[icon]}${count > 1 ? `×${count}` : ""}`;
                    }).join(" ")}
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>

        <div className="bg-slate-800/60 rounded-2xl border border-white/5 p-4 text-sm text-white/50 leading-relaxed">
          <div className="font-bold text-white/70 mb-2">{t.endgame.whatTaught}</div>
          <ul className="space-y-1 list-disc list-inside">
            {t.endgame.lessons.map((lesson, i) => <li key={i}>{lesson}</li>)}
          </ul>
        </div>

        <AdUnit />

        <Button onClick={resetGame} size="lg" className="w-full">{t.endgame.playAgain}</Button>
      </motion.div>
    </div>
  );
}
